/**
 * Conversion Engine — booth check-in service (Stage 2a).
 *
 * A USLS rep at a booth types an invite voucher code, confirms the member, and
 * toggles what happened (attended / brought a friend / friend joined USLS). This
 * service:
 *   - `lookupCheckinVoucher(code)` — read-only populate of member info (ONLINE
 *     only; offline the rep submits blind, per the MVP design).
 *   - `submitCheckin(input)` — calls the SECURITY DEFINER RPC
 *     `process_voucher_checkin`, which records attendance + catalysts and
 *     escalates the voucher value SERVER-SIDE from the matrix. The client never
 *     sends a dollar amount — only facts.
 *
 * The RPC is idempotent on `client_idempotency_key`, so an offline replay is
 * safe: a duplicate returns success with `value_escalated=false`. That makes the
 * offline outbox (`checkinQueue.ts`) a thin wrapper — no "already processed"
 * special-casing is needed; a replay simply succeeds.
 */

import { supabase } from "./supabase";

export type CheckinSource = "booth_online" | "booth_offline_sync";

/** Facts only — NEVER a dollar amount. Mirrors the offline-queue payload shape. */
export interface CheckinInput {
  event_id: string;
  voucher_code: string;
  attended: boolean;
  brought_friend: boolean;
  friend_joined: boolean;
  client_idempotency_key: string;
  source?: CheckinSource;
}

export interface CheckinContact {
  id?: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface CheckinResultData {
  voucher_id: string;
  voucher_code: string;
  attendee_id: string | null;
  activation_state: string | null;
  current_value: number | null;
  matrix_version: number;
  value_escalated: boolean;
  contact: CheckinContact;
  catalysts: {
    attended: boolean;
    brought_friend: boolean;
    friend_joined_pending: boolean;
  };
}

export interface CheckinResult {
  success: boolean;
  failureKind?: "network" | "business";
  message?: string;
  data?: CheckinResultData;
}

export interface LookupResult {
  found: boolean;
  /** Present only when found. */
  voucher?: {
    id: string;
    voucher_code: string;
    event_id: string | null;
    conversion_engine_id: string | null;
    activation_state: string | null;
    current_value: number | null;
    is_cancelled: boolean | null;
    isConversion: boolean;
  };
  contact?: CheckinContact;
  /** A human reason when the code can't be checked in (cancelled, legacy, …). */
  blockReason?: string;
  /** Network vs business so the caller can decide whether to allow blind submit. */
  failureKind?: "network" | "business";
  message?: string;
}

/**
 * Translate a Postgres RAISE message / PostgREST error into a network-vs-business
 * classification. RPC RAISE codes (voucher_not_found, permission_denied, …) are
 * business — retrying won't help. Fetch/timeout failures are network — the
 * offline queue should retry.
 */
function classify(message: string | undefined): "network" | "business" {
  const m = (message || "").toLowerCase();
  if (
    m.includes("failed to fetch") ||
    m.includes("networkerror") ||
    m.includes("network error") ||
    m.includes("timeout") ||
    m.includes("timed out") ||
    m.includes("connection")
  ) {
    return "network";
  }
  return "business";
}

const BUSINESS_MESSAGES: Record<string, string> = {
  voucher_not_found: "No voucher found for that code.",
  voucher_cancelled: "This voucher has been cancelled.",
  voucher_redeemed:
    "This voucher has already been redeemed — it can't be checked in.",
  not_conversion_voucher: "That code isn't a Conversion Engine invite voucher.",
  voucher_event_mismatch: "That voucher belongs to a different event.",
  permission_denied: "You don't have permission to check members in.",
  not_authenticated: "Your session expired — please sign in again.",
  no_active_matrix_version:
    "No active reward matrix is configured for this voucher yet.",
  no_catalyst_reported: "Toggle at least one outcome before submitting.",
  idempotency_key_required: "Internal error: missing idempotency key.",
  "reused with a different check-in payload":
    "This check-in was already submitted with different details.",
  // generate-ticket (Stage 2b)
  event_not_found: "That event no longer exists.",
  no_conversion_engine_for_event:
    "This event isn't set up for Conversion Engine rewards yet.",
  contact_not_found: "That member record could not be found.",
  first_name_required: "Enter the member's first name.",
  email_required: "Enter the member's email.",
  code_generation_failed: "Could not generate a unique code — try again.",
  // link-friend (Stage 3a)
  inviter_voucher_not_found: "The inviting member's voucher wasn't found.",
  inviter_not_conversion_voucher:
    "The inviting member's code isn't a Conversion Engine voucher.",
  inviter_is_reward_voucher:
    "That code is a reward voucher and can't be used as the inviter.",
  inviter_voucher_cancelled: "The inviting member's voucher is cancelled.",
  inviter_event_mismatch: "The inviting member's voucher is for another event.",
  friend_not_found:
    "No member found for that friend's email/code — they need a ticket first.",
  friend_identifier_required: "Enter the friend's email or code.",
  cannot_invite_self: "A member can't be their own invited friend.",
  inviter_already_has_friend:
    "This member is already credited with a different joined friend for this event.",
  friend_already_linked:
    "That friend is already credited to another inviting member.",
  reward_voucher_not_checkinable:
    "That's a reward voucher — it can't be checked in.",
};

function humanize(message: string | undefined): string {
  const raw = (message || "").trim();
  for (const key of Object.keys(BUSINESS_MESSAGES)) {
    if (raw.includes(key)) {
      return BUSINESS_MESSAGES[key];
    }
  }
  return raw || "Check-in failed.";
}

/**
 * Read-only member populate for the booth UI. Online only — if it fails on the
 * network the caller may still allow a blind offline submit.
 */
export async function lookupCheckinVoucher(
  code: string,
): Promise<LookupResult> {
  const normalized = (code || "").trim().toUpperCase();
  if (!normalized) {
    return { found: false, failureKind: "business", message: "Enter a code." };
  }
  try {
    // Exact-code lookup via the scoped, rate-limited voucher-lookup edge fn instead of a direct anon
    // `from("vouchers")` read (security audit: direct anon reads enumerated all voucher codes + emails).
    const { data: lk, error } = await supabase.functions.invoke(
      "voucher-lookup",
      {
        body: { code: normalized },
      },
    );
    const voucher =
      (lk as { voucher: Record<string, any> | null } | null)?.voucher ?? null;

    if (error) {
      return {
        found: false,
        failureKind: classify(error.message),
        message: humanize(error.message),
      };
    }
    if (!voucher) {
      return {
        found: false,
        failureKind: "business",
        message: BUSINESS_MESSAGES.voucher_not_found,
      };
    }

    const isConversion = Boolean(voucher.conversion_engine_id);
    let contact: CheckinContact = { email: voucher.email };
    if (voucher.contact_id) {
      const { data: c } = await supabase
        .from("contacts")
        .select("id, first_name, last_name, email, phone_number, mobile_phone")
        .eq("id", voucher.contact_id)
        .maybeSingle();
      if (c) {
        contact = {
          id: c.id,
          first_name: c.first_name,
          last_name: c.last_name,
          email: c.email,
          phone: c.mobile_phone || c.phone_number,
        };
      }
    }

    let blockReason: string | undefined;
    if (voucher.is_cancelled) {
      blockReason = BUSINESS_MESSAGES.voucher_cancelled;
    } else if (!isConversion) {
      blockReason = BUSINESS_MESSAGES.not_conversion_voucher;
    }

    return {
      found: true,
      voucher: {
        id: voucher.id,
        voucher_code: voucher.voucher_code,
        event_id: voucher.event_id,
        conversion_engine_id: voucher.conversion_engine_id,
        activation_state: voucher.activation_state,
        current_value: voucher.current_value,
        is_cancelled: voucher.is_cancelled,
        isConversion,
      },
      contact,
      blockReason,
    };
  } catch (err) {
    return {
      found: false,
      failureKind: "network",
      message: err instanceof Error ? err.message : "Lookup failed.",
    };
  }
}

/**
 * Submit a booth check-in. Idempotent server-side on `client_idempotency_key`.
 */
export async function submitCheckin(
  input: CheckinInput,
): Promise<CheckinResult> {
  if (
    !input.client_idempotency_key ||
    input.client_idempotency_key.length < 8
  ) {
    return {
      success: false,
      failureKind: "business",
      message: "Internal error: missing idempotency key.",
    };
  }
  try {
    const { data, error } = await supabase.rpc("process_voucher_checkin", {
      p_event_id: input.event_id,
      p_voucher_code: input.voucher_code.trim().toUpperCase(),
      p_attended: input.attended,
      p_brought_friend: input.brought_friend,
      p_friend_joined: input.friend_joined,
      p_client_idempotency_key: input.client_idempotency_key,
      p_source: input.source ?? "booth_online",
    });

    if (error) {
      return {
        success: false,
        failureKind: classify(error.message),
        message: humanize(error.message),
      };
    }
    return { success: true, data: data as CheckinResultData };
  } catch (err) {
    return {
      success: false,
      failureKind: "network",
      message: err instanceof Error ? err.message : "Check-in failed.",
    };
  }
}

// ---------------------------------------------------------------------------
// Stage 2b — generate a ticket (mint an invite voucher) for a no-code walk-in.
// Online only by nature (mints server rows + a unique code).
// ---------------------------------------------------------------------------

export interface GenerateTicketInput {
  event_id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  /** When the rep already resolved an existing contact. */
  contact_id?: string;
  client_idempotency_key: string;
}

export interface GenerateTicketResult {
  success: boolean;
  failureKind?: "network" | "business";
  message?: string;
  data?: {
    voucher_code: string;
    voucher_id: string;
    reused: boolean;
    /** Opaque per-voucher token for the member's "forward to a friend" link (Step 3). */
    referral_token: string | null;
    contact: CheckinContact;
  };
}

export async function generateTicket(
  input: GenerateTicketInput,
): Promise<GenerateTicketResult> {
  if (
    !input.client_idempotency_key ||
    input.client_idempotency_key.length < 8
  ) {
    return {
      success: false,
      failureKind: "business",
      message: "Internal error: missing idempotency key.",
    };
  }
  try {
    const { data, error } = await supabase.rpc("generate_conversion_ticket", {
      p_event_id: input.event_id,
      p_email: input.email ? input.email.trim() : null,
      p_first_name: input.first_name ? input.first_name.trim() : null,
      p_last_name: input.last_name ? input.last_name.trim() : null,
      p_phone: input.phone ? input.phone.trim() : null,
      p_contact_id: input.contact_id ?? null,
      p_client_idempotency_key: input.client_idempotency_key,
    });
    if (error) {
      return {
        success: false,
        failureKind: classify(error.message),
        message: humanize(error.message),
      };
    }
    return {
      success: true,
      data: data as GenerateTicketResult["data"],
    };
  } catch (err) {
    return {
      success: false,
      failureKind: "network",
      message:
        err instanceof Error ? err.message : "Could not generate a ticket.",
    };
  }
}

// ---------------------------------------------------------------------------
// Stage 3a — link a joined friend to the inviting member (online only).
// Records the friend on the inviter's friend_joined catalyst. NO value moves
// here; activation happens at membership confirmation (Stage 3b).
// ---------------------------------------------------------------------------

export interface LinkFriendInput {
  inviter_voucher_code: string;
  /** One of email / voucher code identifies the friend. */
  new_member_email?: string;
  new_member_voucher_code?: string;
  event_id: string;
  client_idempotency_key: string;
}

export interface LinkFriendResult {
  success: boolean;
  failureKind?: "network" | "business";
  message?: string;
  data?: {
    catalyst_id: string;
    inviter_voucher_id: string;
    new_member_contact_id: string;
    state: string;
    reused: boolean;
    friend_already_active: boolean;
    activation_enqueued: boolean;
    new_member: CheckinContact;
  };
}

export async function linkFriend(
  input: LinkFriendInput,
): Promise<LinkFriendResult> {
  if (
    !input.client_idempotency_key ||
    input.client_idempotency_key.length < 8
  ) {
    return {
      success: false,
      failureKind: "business",
      message: "Internal error: missing idempotency key.",
    };
  }
  const email = input.new_member_email?.trim();
  const vcode = input.new_member_voucher_code?.trim();
  if (!email && !vcode) {
    return {
      success: false,
      failureKind: "business",
      message: BUSINESS_MESSAGES.friend_identifier_required,
    };
  }
  try {
    const { data, error } = await supabase.rpc("link_conversion_friend", {
      p_inviter_voucher_code: input.inviter_voucher_code.trim().toUpperCase(),
      p_new_member_email: email || null,
      p_new_member_voucher_code: vcode ? vcode.toUpperCase() : null,
      p_event_id: input.event_id,
      p_client_idempotency_key: input.client_idempotency_key,
    });
    if (error) {
      return {
        success: false,
        failureKind: classify(error.message),
        message: humanize(error.message),
      };
    }
    return { success: true, data: data as LinkFriendResult["data"] };
  } catch (err) {
    return {
      success: false,
      failureKind: "network",
      message:
        err instanceof Error ? err.message : "Could not link the friend.",
    };
  }
}

/** Stable idempotency key generator for a booth submission. */
export function newCheckinIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `chk-${crypto.randomUUID()}`;
  }
  return `chk-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}
